import { Injectable } from '@angular/core';

import { ServerApi } from '@services/server-api';
import { BaseService } from './global-support-base.service';

import { FormControl } from '@angular/forms/forms';
import { ICountryCode, IState, IStateWithCountry } from './global-support.interface';
import { CustomValidators } from '@services/custom-validators';
import { rawPhoneFormat } from '@lib/utils';

@Injectable({
  providedIn: 'root',
})
export class CountryCodesService extends BaseService {
  private countryMap: Record<string, ICountryCode> = {};
  private stateMapByCountryCode: Record<string, IState[]> = {};
  private statesMap: Record<string, IStateWithCountry> = {};
  public countries: ICountryCode[] = [];
  public enableSearch: boolean = false;

  constructor(api: ServerApi) {
    super(api);
    this.getCountries();
  }

  private getCountries(): void {
    this.timeZoneCountryData$.subscribe(data => {
      this.countries = data.countries;
      this.countryMapInit();
      this.stateMapByCountryCode = data.states;
      this.enableSearch = data.countries.length > 10;
      this.stateInit();
    });
  }

  private countryMapInit(): void {
    this.countryMap = {};
    this.countries.forEach(country => {
      const phoneNumberMask = this.transformMasking(country.phoneNumberMask[0])
      // TOOD: Custom regex generator until correct validator pattern is being returned from BE
      const phoneNumberMaskRegex = this.generateMaskRegex(phoneNumberMask)
      this.countryMap[country.code] = {
        ...country,
        phoneNumberMask,
        phoneNumberMaskRegex,
      };
    });
  }

  private stateInit(): void {
    this.statesMap = Object.entries(this.stateMapByCountryCode).reduce((acc, [countryCode, states]) => {
      states.forEach(state => {
        acc[state.code] = {
          ...state,
          countryCode,
        };
      });
      return acc;
    }, {});
  }

  private transformMasking(mask: string): string[] {
    const output = [];
    for (const char of mask.split(' ')) {
      if (char === 'd') {
        output.push(/\d/);
      } else {
        output.push(char);
      }
    }
    return output;
  }

  private generateMaskRegex(mask: Array<RegExp | string>): RegExp {
    let pattern = '';
    let i = 0;

    while (i < mask.length) {
      const currentItem = mask[i];
      if (currentItem instanceof RegExp) {
        const currentPatternSource = currentItem.source;
        let count = 0;
        // Look ahead to count how many times this exact RegExp repeats.
        while (
          i < mask.length &&
          mask[i] instanceof RegExp &&
          (mask[i] as RegExp).source === currentPatternSource
        ) {
          count++;
          i++;
        }
        pattern += `${currentPatternSource}{${count}}`;
      } else {
        pattern += currentItem;
        i++;
      }
    }
    
    const finalPattern = `^${pattern}$`;
    return new RegExp(finalPattern);
  }

  public getCountryByCode(code: string): ICountryCode {
    return this.countryMap[code ?? 'US'];
  }

  public getStateByCode(code: string): IStateWithCountry {
    return this.statesMap[code];
  }

  public updatePhoneNumberValidation(
    code: string,
    validatorFactory: CustomValidators,
    phoneControl: FormControl
  ) {
    const country = this.getCountryByCode(code);

    // TODO: Improve this
    // Manual masking to update input before [textMask] directive runs
    const applyMask = (currentValue: string): string => {
      const rawDigits = rawPhoneFormat(currentValue)
      const newMask = country.phoneNumberMask as Array<RegExp | string>;
      let newlyMaskedValue = '';
      let digitIndex = 0;
      for (const maskChar of newMask) {
        if (digitIndex >= rawDigits.length) {
          break;
        }
        if (maskChar instanceof RegExp) {
          newlyMaskedValue += rawDigits[digitIndex];
          digitIndex++;
        } else {
          newlyMaskedValue += maskChar;
        }
      }

      return newlyMaskedValue;
    };

    if (country && phoneControl) {
      const validator = validatorFactory.intlPhoneFormat(country?.phoneNumberMaskRegex);
      phoneControl.setValidators(validator);
      
      const newlyFormattedValue = applyMask(phoneControl.value);
      phoneControl.setValue(newlyFormattedValue);
    }
  }
}